import type { Attachment, ToolPart } from '@elara/domain/models/ChatMessageModelSpec';
import type { MessagePart, ReasoningPart } from '@elara/domain/models/schemas/message-schemas';
import type { SkillRoute } from '@elara/domain/schemas/skill-route-schema';

import type { ChatMessage } from '#models/ChatMessageModel.js';
import type { ToolCall } from '#models/ToolCallModel.js';
import type { ResourceMetadata } from '../models/ResourceMetadataModel.js';
import type { ChatsState } from './ChatsState.js';

import { findLatestInProgressToolCall, mergeAdjacentTextParts } from '@elara/client-core/chat/message-parts';
import { toolCallHasVisibleLabel } from '@elara/client-core/chat/tool-badges/registry';
import { quickHash } from '@elara/core/utils';
import { extractAttachments } from '@elara/domain/models/ChatMessageModelSpec';
import { extractText, isReasoningPart, isToolPart } from '@elara/domain/models/schemas/message-schemas';
import { parseSkillRouteResource } from '@elara/domain/schemas/skill-route-schema';

import { createState } from '#lib/signals-utils.js';

export function createMessageUiState(msg: ChatMessage, chatsState: ChatsState) {
  const state = createState({
    isDebugPanelExpanded: false,
    isHovered: false,
    isExpanded: false,
    selectedImageIndex: null as number | null,
    toggleDebugPanel(): void {
      this.isDebugPanelExpanded = !this.isDebugPanelExpanded;
    },
    setHovered(isHovered: boolean): void {
      this.isHovered = isHovered;
    },
    toggleExpanded(): void {
      this.isExpanded = !this.isExpanded;
    },
    setSelectedImage(index: number | null): void {
      this.selectedImageIndex = index;
    },
    get viewId(): string {
      return msg.id ?? `tmp-msg-${quickHash(`${msg.chatId}-${msg.createdAt.getTime()}-${msg.sequence}`)}`;
    },
    get isStreaming(): boolean {
      return msg.isStreaming;
    },
    get toolParts(): ToolPart[] {
      return this.content.filter((part) => isToolPart(part));
    },
    get hasToolContent(): boolean {
      return this.toolParts.length > 0;
    },
    get attachments(): Attachment[] {
      return extractAttachments(this.content);
    },
    get content(): MessagePart[] {
      return mergeAdjacentTextParts(msg.content);
    },
    get textContent(): string | undefined {
      return extractText(this.content);
    },
    get hasVisibleContent(): boolean {
      const hasText = this.textContent != null && this.textContent.trim().length > 0;
      const latestInProgressId = this.latestInProgressToolCall?.id;
      const hasLabeledToolCall = this.purelyToolCalls.some(
        (tc) =>
          toolCallHasVisibleLabel(tc) && tc.id !== latestInProgressId && !tc.isActive,
      );
      return hasText || this.widgets.length > 0 || hasLabeledToolCall;
    },
    /** All tool calls for this message (including widgets) */
    get toolCalls(): ToolCall[] {
      return chatsState.toolCallsByMessageId[msg.id] ?? [];
    },
    /** Tool calls that are not widgets */
    get purelyToolCalls(): ToolCall[] {
      return this.toolCalls.filter((tc) => !tc.isWidget);
    },
    /** Tool calls that are widgets */
    get widgets(): ToolCall[] {
      return this.toolCalls.filter((tc) => tc.isWidget);
    },
    /** Latest in-progress tool call by content order. Shown as thinking text; omitted from inline badges. */
    get latestInProgressToolCall(): ToolCall | undefined {
      return findLatestInProgressToolCall(this.content, this.toolCalls);
    },
    get debugResourceMetadata(): ResourceMetadata[] {
      const query = chatsState.resourceMetadataQuery(msg.id);
      return query.items;
    },
    /** Skills the router injected for this reply, from its persisted "Skill routing" resource entry. */
    get skillRoute(): SkillRoute | undefined {
      return parseSkillRouteResource(this.debugResourceMetadata.map((resource) => resource.resourceMeta));
    },
    get isThinking(): boolean {
      return this.isStreaming || !this.textContent;
    },
    get lastReasoningPart(): ReasoningPart | undefined {
      return this.content.toReversed().find((part) => isReasoningPart(part));
    },
    get thinkingText(): string {
      const lastReasoningPart = this.lastReasoningPart;
      if (lastReasoningPart) {
        const parsed = parseReasoning(lastReasoningPart.reasoning);
        return parsed?.subject || 'Thinking...';
      }
      return 'Thinking...';
    },
    get formattedText(): string {
      return this.textContent ?? '';
    },
  });
  return state;
}

export function parseReasoning(reasoning: string): { subject: string; thought: string } | undefined {
  const match = reasoning.match(/^\s*\*\*(.*?)\*\*\s+(.*?)\s*$/s);
  if (!match) return undefined;
  const subject = match[1]!;
  const thought = match[2]!;
  return { subject: subject.trim(), thought: thought.trim() };
}

export interface MessageUiState extends ReturnType<typeof createMessageUiState> {}
